import { approvalText } from './approval.js';
import { formatNumberOf } from './aggregate.js';
import type { CustomField } from './types.js';

export class FieldValueError extends Error {}

/**
 * Liest eine Zahl in deutscher oder internationaler Schreibweise – verlustfrei für alles,
 * was editableNumber() ausgibt. Beispiele: „12,50“ „12.5“ „1.234,56“ „1,234.56“ „1.234“ „-3“.
 * Gibt null zurück, wenn die Eingabe nicht eindeutig eine Zahl ist.
 */
export function parseDecimal(input: string): number | null {
  let s = input.replace(/[\s\u00a0\u202f€]/g, '');
  if (!s) return null;
  let sign = 1;
  if (s.startsWith('-') || s.startsWith('−')) {
    sign = -1;
    s = s.slice(1);
  }
  if (!/^[\d.,]+$/.test(s) || !/\d/.test(s)) return null;
  const lastDot = s.lastIndexOf('.');
  const lastComma = s.lastIndexOf(',');
  let intPart: string;
  let frac = '';
  let group: '.' | ',' | null = null;
  if (lastDot >= 0 && lastComma >= 0) {
    // Beide vorhanden: das letzte ist das Dezimalzeichen, das andere trennt Tausender
    const dec = lastDot > lastComma ? '.' : ',';
    group = dec === '.' ? ',' : '.';
    const i = s.lastIndexOf(dec);
    intPart = s.slice(0, i);
    frac = s.slice(i + 1);
    if (frac.includes(group) || intPart.includes(dec)) return null;
  } else if (lastComma >= 0) {
    const parts = s.split(',');
    if (parts.length === 2) [intPart, frac] = parts as [string, string];
    else {
      group = ',';
      intPart = s;
    }
  } else if (lastDot >= 0) {
    const parts = s.split('.');
    // Ein Punkt mit genau drei Ziffern danach („1.234“) ist ein Tausenderpunkt (deutsche Schreibweise)
    if (parts.length === 2 && !(parts[1]!.length === 3 && parts[0]!.length >= 1 && parts[0]!.length <= 3 && parts[0] !== '0'))
      [intPart, frac] = parts as [string, string];
    else {
      group = '.';
      intPart = s;
    }
  } else intPart = s;
  if (group) {
    const groups = intPart.split(group);
    if (!/^\d{1,3}$/.test(groups[0]!) || groups.slice(1).some((g) => !/^\d{3}$/.test(g))) return null;
    intPart = groups.join('');
  }
  if (!/^\d*$/.test(intPart) || !/^\d*$/.test(frac) || (!intPart && !frac)) return null;
  const n = Number(`${intPart || '0'}.${frac || '0'}`) * sign;
  return Number.isFinite(n) ? n : null;
}

/** Darstellung zum Bearbeiten (deutsch, ohne Tausenderpunkte) – parseDecimal liest sie verlustfrei zurück */
export function editableNumber(n: number, currency = false): string {
  return n.toLocaleString('de-DE', {
    useGrouping: false,
    minimumFractionDigits: currency ? 2 : 0,
    maximumFractionDigits: currency ? 2 : 10,
  });
}

/**
 * Prüft und normalisiert einen Feldwert. null/'' löscht den Wert.
 * Gibt den gespeicherten Wert zurück oder wirft FieldValueError.
 */
export function normalizeFieldValue(field: CustomField, value: unknown): unknown {
  if (value === null || value === undefined || value === '') return null;
  switch (field.type) {
    case 'text':
    case 'phone': {
      const s = String(value).trim();
      if (s.length > 500) throw new FieldValueError('Text zu lang (max. 500 Zeichen)');
      return s || null;
    }
    case 'longtext': {
      const s = String(value);
      if (s.length > 10_000) throw new FieldValueError('Text zu lang');
      return s.trim() ? s : null;
    }
    case 'number':
    case 'currency': {
      const n = typeof value === 'number' ? value : parseDecimal(String(value));
      if (n === null || !Number.isFinite(n)) throw new FieldValueError('Keine gültige Zahl');
      return field.type === 'currency' ? Math.round(n * 100) / 100 : n;
    }
    case 'checkbox':
      return value === true || value === 'true' ? true : null;
    case 'date': {
      const s = String(value);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) throw new FieldValueError('Datum im Format JJJJ-MM-TT erwartet');
      return s;
    }
    case 'url': {
      const s = String(value).trim();
      try {
        const u = new URL(/^[a-z]+:\/\//i.test(s) ? s : `https://${s}`);
        if (!['http:', 'https:'].includes(u.protocol)) throw new Error();
        return u.toString();
      } catch {
        throw new FieldValueError('Kein gültiger Link');
      }
    }
    case 'email': {
      const s = String(value).trim().toLowerCase();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)) throw new FieldValueError('Keine gültige E-Mail-Adresse');
      return s;
    }
    case 'rating': {
      const n = Math.round(Number(value));
      if (!(n >= 1 && n <= 5)) throw new FieldValueError('Bewertung 1 bis 5');
      return n;
    }
    case 'select': {
      const id = String(value);
      if (!field.options.some((o) => o.id === id)) throw new FieldValueError('Unbekannte Auswahl');
      return id;
    }
    case 'multiselect': {
      if (!Array.isArray(value)) throw new FieldValueError('Liste erwartet');
      const ids = value.map(String).filter((id) => field.options.some((o) => o.id === id));
      return ids.length ? [...new Set(ids)] : null;
    }
    case 'person':
      return String(value);
    case 'approval':
      throw new FieldValueError('Freigaben gibst du an der Karte über „Freigeben“ oder „Ablehnen“.');
    case 'sequence':
      throw new FieldValueError('Die laufende Nummer vergibt KaBa selbst – sie lässt sich nicht ändern.');
    case 'formula':
      throw new FieldValueError('Berechnete Felder rechnet KaBa selbst aus.');
  }
}

const eur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const num = new Intl.NumberFormat('de-DE');

/** Kurzdarstellung für Kartenvorderseite und Liste */
export function formatFieldValue(field: CustomField, value: unknown, people?: Map<string, { name: string }>): string | null {
  if (value === null || value === undefined) return null;
  switch (field.type) {
    case 'currency':
    case 'number':
    case 'formula':
      // 0.82: Währung, Einheit und Nachkommastellen aus den Feld-Einstellungen
      return Number.isFinite(Number(value)) ? (field.config ? formatNumberOf(field, Number(value)) : field.type === 'currency' ? eur.format(Number(value)) : num.format(Number(value))) : null;
    case 'checkbox':
      return value ? '✓' : null;
    case 'date': {
      const [y, m, d] = String(value).split('-');
      return `${d}.${m}.${y}`;
    }
    case 'rating':
      return '★'.repeat(Number(value));
    case 'select':
      return field.options.find((o) => o.id === value)?.label ?? null;
    case 'multiselect':
      return (value as string[])
        .map((id) => field.options.find((o) => o.id === id)?.label)
        .filter(Boolean)
        .join(', ');
    case 'person':
      return people?.get(String(value))?.name ?? null;
    case 'approval':
      return approvalText(value, people);
    case 'url':
      try {
        return new URL(String(value)).hostname;
      } catch {
        return String(value);
      }
    default:
      return String(value);
  }
}

/** Höchstzahl angehefteter Felder je Board (Übersicht der Kartendetails bleibt kurz) */
export const MAX_PINNED_FIELDS = 3;

/** 0.81: Laufende Nummer formatieren – „R-{JJJJ}-“ wird zu „R-2026-“, Nummer mit führenden Nullen */
export function formatSequence(cfg: { prefix: string; digits: number }, n: number, year: number): string {
  return `${cfg.prefix.replaceAll('{JJJJ}', String(year)).replaceAll('{JJ}', String(year).slice(2))}${String(n).padStart(cfg.digits, '0')}`;
}
